import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IGenericError,
  IReferenceText,
  IReferenceTextBase,
  IReferenceTextRaw,
} from '../types'

const referenceTextsCollectionName = 'referenceTexts'

const referenceTextsCollectionRef = collection(
  firebaseDb,
  referenceTextsCollectionName,
)

const referenceTextsDocRef = (id: string) =>
  doc(firebaseDb, referenceTextsCollectionName, id)

export async function createReferenceText(
  referenceText: IReferenceTextBase,
): Promise<string> {
  try {
    const isReferenceIdUnique: boolean = await checkReferenceIdIsUnique(referenceText)

    if (!isReferenceIdUnique) {
      throw new Error(
        `ReferenceText with referenceId: "${referenceText.referenceId}" already exists`,
      )
    }

    const document = await addDoc(referenceTextsCollectionRef, {
      ...referenceText,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return document.id
  } catch (error: unknown) {
    console.error(
      `error while trying to create reference text :>> `,
      (error as IGenericError)?.message || error,
      referenceText,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create reference text`,
    )
  }
}

export async function getReferenceTextById(
  id: string,
): Promise<IReferenceText | undefined> {
  try {
    const docRef = referenceTextsDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as DocumentSnapshot<IReferenceTextRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IReferenceText = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while trying to get reference text by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get reference text`,
    )
  }
}

export async function getReferenceTextByReferenceId(
  referenceId: string,
): Promise<IReferenceText | undefined> {
  try {
    const q = query(referenceTextsCollectionRef, where('referenceId', '==', referenceId), limit(1))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return undefined
    }

    const docSnap = querySnapshot.docs[0]
    const data = docSnap.data() as IReferenceTextRaw

    const doc: IReferenceText = {
      id: docSnap.id,
      ...data,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while trying to get reference text by referenceId: ${referenceId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get reference text by referenceId`,
    )
  }
}

export async function getReferenceTexts(
  queryConstraints: QueryConstraint[] = [],
): Promise<IReferenceText[]> {
  try {
    const q = query(referenceTextsCollectionRef, ...queryConstraints)
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IReferenceTextRaw>

    let documents: IReferenceText[] = []

    querySnapshot.forEach((snapshot) => {
      documents.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(
      `error while trying to get reference text :>> `,
      (error as IGenericError)?.message || error,
      queryConstraints,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get reference text`,
    )
  }
}

export async function updateReferenceTextById(
  referenceTextId: string,
  updateObj: IReferenceTextBase,
): Promise<void> {
  try {
    const docRef = referenceTextsDocRef(referenceTextId)

    await updateDoc(docRef, { ...updateObj, updatedAt: serverTimestamp() })
  } catch (error: unknown) {
    console.error(
      `error while trying to update reference text by id: ${referenceTextId} :>> `,
      (error as IGenericError)?.message || error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update reference text`,
    )
  }
}

export async function deleteReferenceTextById(
  referenceTextId: string,
): Promise<void> {
  try {
    const docRef = referenceTextsDocRef(referenceTextId)

    await deleteDoc(docRef)
  } catch (error: unknown) {
    console.error(
      `error while trying to delete reference text by id: ${referenceTextId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete reference text`,
    )
  }
}

async function checkReferenceIdIsUnique(
  referenceText: IReferenceTextBase,
): Promise<boolean> {
  const queryConstraints: QueryConstraint[] = [
    where('referenceId', '==', referenceText.referenceId),
  ]

  const referenceTexts: IReferenceText[] = await getReferenceTexts(
    queryConstraints,
  )

  return !!referenceTexts.length ? false : true
}
